import {Card, Typography, Timeline, Tooltip, Divider, Spin, Form, Space, DatePicker} from 'antd';
import React, {useState, useEffect} from 'react';
import {useDataProvider, useQuery} from 'react-admin';
import {formatDateAndTime, formatDateAndTimeFromNow} from "@src/helpers/utils";
import DebounceSelect from "@src/components/Common/DebounceSelect";
import {ExtendedDataProvider} from "@src/types";

const { Text } = Typography;
const { RangePicker } = DatePicker;


const HighlightText = (text: string, keywords: any) => {
    if (!keywords || keywords.length === 0) return <p>{text}</p>
    const regex = new RegExp(`(${keywords.join("|")})`, "gi");
    const parts = text.split(regex);

    return (
        <>
            {
                parts.map((part, index) => keywords.some((keyword:string) =>
                    part.toLowerCase() === keyword.toLowerCase()) ? <Text code>{part}</Text> : part)
            }
        </>
    );
};


const renderMessage = (activity: any) => {
    const keywords = Object.values(activity?.extra_data)
    const roles = ["ADMIN", "GENERAL", "READ_ONLY", "SYSTEM"]
    keywords.push(...roles)
    if(activity.history_username)
        keywords.push(activity.history_username)

    return <>
        {HighlightText(activity.message, keywords)}
        <Tooltip title={formatDateAndTime(activity.history_date)}>
            <Text type="secondary">{"  " + formatDateAndTimeFromNow(activity.history_date)}</Text>
        </Tooltip>
    </>
}


const FilterActivity = ({filters, setFilters}: {filters: any, setFilters: Function}) => {
    const dataProvider: ExtendedDataProvider = useDataProvider()

    const fetchGroupList = async (values: string): Promise<any> => {
        return dataProvider.getList(
            'team_memberships',
            {
                pagination: { page: 1, perPage: 20 },
                sort: { field: 'id', order: 'dsc' },
                filter: {"username": values}
            })
            .then((data: any) => {
                const usersList: any[] = [];
                data.data.map((result: any) => {
                    usersList.push({label: result.username, value: result.username})
                })
                return usersList
            })
    }

    const timeRangeOnChange = (dates: any , dateStrings: any) => {
        let rangeStart = '';
        let rangeEnd = '';
        if (dates) {
            if (dates[0]) {
                const rangeStart_ = dates[0];
                rangeStart_.set ('hour', 0);
                rangeStart_.set ('minute', 0);
                rangeStart_.set ('second', 0);
                rangeStart_.set ('millisecond', 0);
                rangeStart = rangeStart_.toISOString ()
            }

            if (dates[1]) {
                const rangeEnd_ = dates[1]
                rangeEnd_.set ('hour', 23);
                rangeEnd_.set ('minute', 59);
                rangeEnd_.set ('second', 59);
                rangeEnd_.set ('millisecond', 0);
                rangeEnd = rangeEnd_.toISOString ()
            }
        }

        setFilters({
            ...filters,
            history_date_after: rangeStart,
            history_date_before: rangeEnd
        }, {});
    }


    return (
        <Form className="row-col" style={{marginBottom: "15px"}}>
            <Space orientation={"horizontal"}>
                <DebounceSelect
                    id={"1"}
                    createable
                    showSearch
                    allowClear
                    value={filters?.history_username}
                    onChangeSelect={newValue => {
                        setFilters({
                            ...filters,
                            // @ts-ignore
                            history_username: newValue?.value
                        }, {});
                    }}
                    placeholder="Select User"
                    fetchOptions={fetchGroupList}
                    style={{ width: '250px' }}
                />
                Filter by date:
                <RangePicker onChange={timeRangeOnChange} allowEmpty={[true, true]}/>
            </Space>
        </Form>
    );
}


const TeamActivity = ({teamId}: {teamId: number}) => {
    const [activities, setActivities] = useState<any[]>([]);
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [filters, setFilters] = useState({});

    const limit = 20;
    const spinnerElement = <Spin size="large" tip="Loading"><Card style={{height: "100px"}}></Card></Spin>

    const { data, loaded, error, loading } = useQuery({
        type: 'getAction',
        resource: "teams",
        payload: {
            id: teamId,
            verb: 'activity',
            filter: {
                limit: limit, offset: offset, ...filters
            }
        }
    });
    useEffect(() => {
        setActivities([])
        setOffset(0)
        setHasMore(true)
    }, [filters]);

    useEffect(() => {
        if (loaded && data?.results) {
            setActivities((prev) => [...prev, ...data.results]);
            setHasMore(data.results.length === limit); // Check if there are more results to load
        }
    }, [data, loaded]);

    const loadMore = () => {
        if (!loading && hasMore) {
            setOffset((prevOffset) => prevOffset + limit);
        }
    };

    return (
        <Card
            bordered={false}
            bodyStyle={{ padding: '20px' }}
            className="criclebox tablespace mb-24"
        >
            {
                data?
                    <>
                        <FilterActivity filters={filters} setFilters={setFilters} />
                        <Timeline>
                            {Object.values(activities).map((activity: any) => <Timeline.Item>{renderMessage(activity)}</Timeline.Item>)}
                        </Timeline>
                        {hasMore && (
                            <Divider plain>
                                <a onClick={loadMore}>{loading ? "Loading..." : "Load More"}</a>
                            </Divider>
                        )}
                    </>
                :
                    spinnerElement
            }
        </Card>
    )
}

export default TeamActivity;
